import { StorymapCollaboratorModel } from '@/api/common';
import { Query } from '@/_core/cqrs/types/queries';
import { BoardUserActivity } from '@/api/workspaceSettings/queries';
import { PublishedStorymapAccessKind } from '@/pages/main/storymap/utils/publishedStorymapRoutes';

export class StorymapCardDetailsQuery extends Query<StorymapCardDetailsResult> {
    public constructor(public CardId: string, public StoryMapSlug: string) {
        super('StorymapCardDetailsQuery');
    }
}

export class PublishedStorymapCardDetailsQuery extends Query<PublishedStorymapCardDetailsResult> {
    public constructor(public AccessKind: PublishedStorymapAccessKind, public AccessKey: string, public CardId: string) {
        super('PublishedStorymapCardDetailsQuery');
    }
}

export class StorymapCardAiAdHocQuery extends Query<StorymapCardAiAdHocQueryResult> {
    public constructor(public CardId: string, public StoryMapId: string, public CurrentCardDescription: string, public AdHocQuery: string) {
        super('StorymapCardAiAdHocQuery');
    }
}

export class StorymapCardAiRefineQuery extends Query<StorymapCardAiAdHocQueryResult> {
    public ConversationHistory: { Request: string; Response: string }[];

    public constructor(
        public CardId: string,
        public StoryMapId: string,
        public CurrentCardDescription: string,
        public AdHocQuery: string,
        conversationHistory: {
            request: string;
            response: string;
            promptType: number;
            templateId?: string;
        }[]
    ) {
        // Map the lowercase properties to capitalized ones for .NET conventions
        const capitalizedConversationHistory = conversationHistory.map((item) => ({
            Request: item.request,
            Response: item.response,
            PromptType: item.promptType,
            TemplateId: item.templateId ?? null,
        }));

        super('StorymapCardAiRefineQuery');
        this.ConversationHistory = capitalizedConversationHistory;
    }
}

export class StorymapCardCustomAiTemplateQuery extends Query<StorymapCardAiAdHocQueryResult> {
    public constructor(
        public CardId: string,
        public StoryMapId: string,
        public TemplateId: string,
        public CurrentCardDescription: string,
        public IsLibraryTemplate: boolean = false
    ) {
        super('StorymapCardCustomAiTemplateQuery');
    }
}

export class StorymapCardHistoryQuery extends Query<StorymapCardHistoryResult> {
    public constructor(public CardId: string, public StoryMapId: string) {
        super('StorymapCardHistoryQuery');
    }
}

export interface StorymapCardHistoryResult {
    Activities: BoardUserActivity[];
}

export class StorymapLinkedExternalCardQuery extends Query<StorymapLinkedExternalCardQueryResult> {
    public constructor(public Id: string, public StoryMapSlug: string) {
        super('StorymapLinkedExternalCardQuery');
    }
}

export class PublishedStorymapLinkedExternalCardQuery extends Query<StorymapLinkedExternalCardQueryResult> {
    public constructor(
        public AccessKind: PublishedStorymapAccessKind,
        public AccessKey: string,
        public TargetStoryMapSlug: string,
        public CardId: string
    ) {
        super('PublishedStorymapLinkedExternalCardQuery');
    }
}

export interface StorymapLinkedExternalCardQueryResult {
    Id: string;
    Title: string;
    StorymapTitle: string;
    Color: CardColor;
    Key: string;
    LinkedItemKey: string;
    IsSubtask: boolean;
    IsTask: boolean;
    IsActivity: boolean;
    IsCardKeyEnabled: boolean;
    IsTrashed: boolean;
    IsLinkedToIntegrationTool: boolean;
    IntegrationToolType: number;
}

export interface StorymapCardDetailsResult {
    CardDetails: CardDetailsQueryResult;
    MentionableUsers: MentionableUser[];
    Statuses: SubtaskStatusModel[];
    Annotations: AnnotationModel[];
    Collaborators: StorymapCollaboratorModel[];
    TaskName: string;
    ActivityName: string;
    IsReadOnly: boolean;
    CanComment: boolean;
    IsCardLinkedToIntegrationTool: boolean;
    IsMissingImpersonationToken: boolean;
    CanBeLinkedToIntegrationTool: boolean;
    LinkedUrl: string;
    IntegrationToolType: number;
    IsCardKeyEnabled: boolean;
    Feedbacks: FeedbackModel[];

    Personas: PersonaModel[];
}

export interface PublishedStorymapCardDetailsResult {
    CardDetails: Partial<CardDetailsQueryResult> & {
        Id: string;
        Key: string;
        StorymapSlug: string;
        Description: string;
        Name: string;
        IsSubtask: boolean;
        IsTask: boolean;
        IsActivity: boolean;
        EstimationUnit: string;
        AssigneeDisplayName?: string;
        HasAssignee?: boolean;
        CreatedAt: string;
        Color: CardColor;
        WebLinks?: CardWeblinkModel[];
        Comments?: CardCommentModel[];
        PersonaIds: string[];
        AnnotationIds: string[];
        Attachments?: CardAttachmentModel[];
    };
    Statuses: SubtaskStatusModel[];
    Annotations: AnnotationModel[];
    Personas: PersonaModel[];
    ActivityName?: string;
    TaskName?: string;
    IsReadOnly: boolean;
    CanComment: boolean;
    CanReadComments: boolean;
    IsCardKeyEnabled: boolean;
}

export interface CardDetailsQueryResult {
    Id: string;
    Key: string;
    StorymapId: string;
    StorymapSlug: string;
    StorymapName: string;
    Name: string;
    Description: string;
    IsSubtask: boolean;
    IsTask: boolean;
    IsActivity: boolean;
    ActivityId: string;
    TaskId: string;
    AssigneeId?: string;
    SubtaskState?: number;
    CreatedAt: string;
    CreatedBy: string;
    ReleaseId?: string;
    IsDeletable: boolean;
    Estimation?: number;
    EstimationUnit: string;
    Attachments: CardAttachmentModel[];
    WebLinks: CardWeblinkModel[];
    Color: CardColor;
    NumberOfChildren: number;
    Comments: CardCommentModel[];
    ViewersCanCommentOnCards: boolean;
    IsReadOnlyAccessOfPublishedBoard: boolean;
    IsPrivateLinkCommentingEnabled: boolean;
    IsPrivateLinkSharedAccess: boolean;
    CurrentUserIsLoggedIn: boolean;
    CurrentUserIsWorkspaceMember: boolean;
    WorkspaceMemberCanCommentOnCard: boolean;
    IsWatched: boolean;
    CardPersonas: CardPersonaModel[];

    CardAnnotations: CardAnnotationModel[];

    LinkedItemKey: string;
    LinkedItemId: string;
    LinkedItemTypeId: string;
    LinkedItemTypeName: string;
}

export interface PersonaModel {
    Id: string;
    Name: string;
    Picture: string;
    Group: string;
    IsGlobal: boolean;
}

interface CardAttachmentModel {
    Id: string;
    Name: string;
    HasThumbnail: boolean;
    Extension: string;
    UploadedBy: string;
    UploadedAt: string;
    FileSize: number;
    // Delivered to published recipients only (omitted for collaborators).
    OpenUrl?: string;
    DownloadUrl?: string;
    ThumbnailUrl?: string;
}

interface CardCommentModel {
    Id: string;
    CreatedBy: string;
    CreatedByUserId: string;
    TimeToDisplay: string;
    IsCurrentUsersComment: boolean;
    CommentText: string;
    IsEdited: boolean;
}

interface CardWeblinkModel {
    Id: string;
    Url: string;
    Name: string;
    FaviconId: string;
    CreatedBy: string;
    CreatedAt?: string;
}

enum CardColor {
    Default = 0,
    Blue = 1,
    Gray = 2,
    Green = 3,
    Orange = 4,
    Pink = 5,
    Purple = 6,
    Red = 7,
    Yellow = 8,
    White = 9,
    Teal = 10,
    Indigo = 11,
    TransparentAnnotation = 99,
}

interface CardPersonaModel {
    PersonaId: string;
}

export interface CardAnnotationModel {
    AnnotationId: string;
}

interface MentionableUser {
    Id: string;
    Name: string;
    Email: string;
    IsCurrentUser: boolean;
}

export interface SubtaskStatusModel {
    Key: number;
    Name: string;
    Color: string;
    Category: StatusCategory;
    Order: number;
}

export interface AnnotationModel {
    Id: string;
    Icon?: AnnotationIconType;
    Emoji: string;
    IsEmoji: boolean;
    Color: AnnotationIconColorType;
    Name: string;
    IsArchived: boolean;
    Order: number;
}

export enum AnnotationIconType {
    Circle = 10,
    Square = 20,
    Clone = 30,
    Diamond = 40,
    Star = 50,
    Heart = 60,
    Bolt = 70,
    Bookmark = 80,
    Bell = 90,
    Thumbtack = 100,
    Stream = 110,
    Ban = 120,
    ParkingCircle = 130,
    TimesCircle = 140,
    MinusCircle = 150,
    Copyright = 160,
    ExclamationTriangle = 170,
    Bug = 180,
    Turtle = 190,
    Whale = 200,
    HandPointer = 210,
    ThumbsUp = 220,
    ThumbsDown = 230,
    FrownOpen = 240,
    Meh = 250,
    Grin = 260,
    GrinHearts = 270,
    Comment = 280,
    Comments = 290,
    Clock = 300,
    AlarmClock = 310,
    HourGlass = 320,
    CalendarAlt = 330,
    Binoculars = 340,
    Megaphone = 350,
    Bullseye = 360,
    BullseyeArrow = 370,
    Lightbulb = 380,
    LightbulbOn = 390,
    Trash = 400,
    HandshakeAlt = 410,
    Flag = 420,
    FlagCheckered = 430,
    Cog = 440,
    Database = 450,
    PuzzlePiece = 460,
    Cubes = 470,
    ShoppingCart = 480,
    Rocket = 490,
    Trophy = 500,
    Lock = 510,
    Unlock = 520,
    Sitemap = 530,
    ProjectDiagram = 540,
    CodeBranch = 550,
    Bomb = 560,
    Gift = 570,
    Medal = 580,
    ChessQueen = 590,
    ChartArea = 600,
    File = 610,
    FileAlt = 620,
    Cloud = 630,
    CloudRain = 640,
    GraduationCap = 650,
    User = 660,
    Users = 670,
    AddressCard = 680,
    Envelope = 690,
    SackDollar = 700,
    EuroSign = 710,
    PoundSign = 720,
    DollarSign = 730,
    Cut = 740,
    Check = 750,
    CheckDouble = 760,
    Question = 770,
    Exclamation = 780,
    Code = 790,
    Anchor = 800,
    HeartRate = 810,
    Snooze = 820,
    TreePalm = 830,
    Minus = 840,
    AngleUp = 850,
    AngleDown = 860,
    AngleDoubleUp = 870,
    AngleDoubleDown = 880,
    Signal1 = 890,
    Signal2 = 900,
    Signal3 = 910,
    Signal4 = 920,
    Signal = 930,
    Bluetooth = 940,
    Wifi = 950,
    Window = 960,
    TachometerSlowest = 970,
    TachometerSlow = 980,
    TachometerAverage = 990,
    TachometerFast = 1000,
    TachometerFastest = 1010,
    MobileAndroid = 1020,
    TabletAndroid = 1030,
    DesktopAlt = 1040,
    AppleAlt = 1050,
    Windows = 1060,
    Android = 1070,
    Linux = 1080,
    Docker = 1090,
    Chrome = 1100,
    Intercom = 1110,
    GoogleDrive = 1120,
}

export enum AnnotationIconColorType {
    Default = 0,
    Red = 1,
    Green = 2,
    Blue = 3,
    Navy = 4,
    Magenta = 5,
    Orange = 6,
    Black = 7,
}

export enum StatusCategory {
    Todo = 10,
    Active = 20,
    Done = 30,
}

export interface StorymapCardAiAdHocQueryResult {
    IsSuccess: boolean;
    Error: string;
    SuggestedText: string;
    Prompt: string;
    LogId: string;
    StatusCode: number;
    PromptType: number;
}

interface FeedbackModel {
    Id: string;
    Content: string;
    CategoryId?: string;
    ContactEmail?: string;
    CreatorToolType: number;
    ExternalToolName?: string;
    IsImportant: boolean;
    IsArchived: boolean;
    AttachmentCount: number;
    CommentCount: number;
    TagIds?: string[];
    CreatedBy?: string;
    CreatedById?: string;
    CreatedAt: string;
}
